/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { IPricingValue } from "../../../../../interfaces/pricingValues";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { createPricingValue, deletePricingValue, listPricingValues, listReasons, showPricingValue, updatePricingValue } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
const DEFAULT_DATA: IPricingValue = {
  id: null,
  idPricingPlan: null,
  externalCode: null, 
  idCustomer: null,
  idService: null,
  type: null,
  idScope: null,
  idItem: null,
  value: '0',
  typeSite: null,
  idRegion: null
};

export const usePricingValues = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
  id: idSelect,
  onSuccess,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
  idService?: string;
  id?: string;
  typeDefault?: string;
  onSuccess?: () => void;
}) => {
  const { idPricingValue: id = idSelect || "new", idPricingPlan } = useParams();
  const [pricingValues, setPricingValues] = useState<IPricingValue[]>([]);
  const [pricingValue, setPricingValue] = useState<IPricingValue>(DEFAULT_DATA);
  const { filter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: defaultLimit || 30 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: `/towermanagement/pricing_plans/${idPricingPlan}/pricing_values`,
    pathBack: `/towermanagement/pricing_plans/${idPricingPlan}`,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePricingValue = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setPricingValue((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      if (key === "type") newData["idReason"] = null;
      return { ...curr, ...newData };
    });
  };

  const remove = async (id: string | number | (string | number)[]) => {
    try {
      setLoading(true);
      
      // Se for um array, remove múltiplas categorias
      if (Array.isArray(id)) {
        await Promise.all(id.map((idPricingValue) => deletePricingValue({ id: idPricingValue })));
        toast.success(`${id.length} Preço(s) removida(s) com sucesso!`);
      } else {
        // Remove apenas uma categoria
        await deletePricingValue({ id });
        toast.success("Preço removido com sucesso!");
      }
      
      await getAll({ page, limit, filter });
    } catch (error: any) {
      console.error("Erro ao remover Preço:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Erro ao Preço categoria";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const getAll = ({
    page = 1,
    limit,
    filter: filterParam,
  }: {
    page?: number;
    limit?: number;
    filter?: Record<string, any>;
  }) => {
    setLoading(true);
    listPricingValues({
      includes: [...includes],
      filter: { ...filter, ...filterParam },
      page,
      limit,
    })
      .then(
        ({ pricingValues: listPricingValues, limit, currentPage, totalItems }) => {
          setPricingValues(listPricingValues);
          
          setLimit(Number(limit));
          setTotalItems(Number(totalItems));
        }
      )
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({
    list: listPricingValues,
    key: "pricingValues",
    includes: ["idSite", ...includes],
    filter,
  });


  const searchReasons = async ({
    page = 1,
    limit,
    filter: filterG = {},
    includes: includesD = [],
  }: {
    page?: number;
    limit?: number;
    filter?: Record<string, any>;
    includes?: string[];
  }) => {
    const data = await listReasons({
      includes: [...includes, ...includesD],
      page,
      filter: { ...filter, ...filterG },
      limit,
    });
    return { items: data.reasons };
  };

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showPricingValue({ id, includes: [] })
      .then(({ pricingValue: pricingValuesShow }) => {
        setPricingValue(pricingValuesShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createPricingValue({ pricingValue })
      .then(() => {
        toast.success(`Valor criado com sucesso!`);
        if (onSuccess) onSuccess();
        else goBack();
      })
      .finally(() => setLoading(false));
  };



  const update = () => {
    setLoading(true);
    updatePricingValue({ id: pricingValue.id!, pricingValue })
      .then(() => {
        toast.success(`Valor atualizada com sucesso!`);
        if (onSuccess) onSuccess();
        else goBack();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter]);


  useEffect(() => {
    if (id === "new") handlePricingValue("idPricingPlan", idPricingPlan);
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  return {
    pricingValues,
    pricingValue,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handlePricingValue,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    searchReasons,
    refetch: () => getAll({ page, limit, filter }),
    remove
  };
};
